import React, { useEffect, useState } from "react";
import l from "components/util/l";
import { useRevisionFormContext } from "components/brief/reviewer/revision/RevisionForm.lib.context";
import View from "components/brief/reviewer/revision/content/annotations/RevisionAnnotationComments.view";

// A comment is anchored either to a mockup, to a tab, or to the section as a whole. The
// title names what it points at, falling back to its position when the target is unnamed.
const getTargetTitle = (annotation, mockups, tabs) => {
  if (annotation.type === "mockup")
    return mockups[annotation.target_idx]?.name || `${l("Mockup%PA418")} ${annotation.target_idx + 1}`;
  if (annotation.type === "tab")
    return tabs[annotation.target_idx]?.name || `${l("Pestaña%PA506")} ${annotation.target_idx + 1}`;
  return l("Adicional%PA505");
};

// Comments are numbered in the order they were placed, and that number is what ties one in
// this list to its marker on the mockup, so the ordering here is not cosmetic.
const RevisionAnnotationComments = ({ currentSection }) => {

  const {
    proposal,
    currentAuthor,
    isDeveloper,
    allowComments,
    showAdditionalCommentButton,
    updateSection,
  } = useRevisionFormContext();

  const sectionIndex = currentSection.sourceIndex;
  const section = proposal.content[sectionIndex] || {};
  const mockups = section.mockups || [];
  const tabs = section.tabs || [];
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [draftText, setDraftText] = useState("");

  const sectionAnnotations = (section.annotations || [])
    .filter((annotation) => annotation.id)
    .filter((annotation) => isDeveloper || (annotation?.visibility_scope || "CLIENT") !== "DEV")
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((annotation, index) => {
      const annotationNumber = index + 1;
      const targetTitle = getTargetTitle(annotation, mockups, tabs);

      return {
        ...annotation,
        key: sectionIndex,
        annotationNumber,
        targetTitle,
      };
    });

  const showCommentsPanel = allowComments || sectionAnnotations.length > 0;

  const updateSectionAnnotations = (getNextAnnotations) => {
    updateSection(sectionIndex, (currentSectionData) => ({
      ...currentSectionData,
      annotations: getNextAnnotations(currentSectionData.annotations || []),
    }));
  };

  const updateAnnotation = (_key, id, data) => {
    updateSectionAnnotations((annotations) =>
      annotations.map((annotation) => annotation.id === id ? { ...annotation, ...data } : annotation)
    );
  };

  const removeAnnotation = (_key, id) => {
    updateSectionAnnotations((annotations) =>
      annotations.filter((annotation) => annotation.id !== id)
    );
  };

  const addAdditionalAnnotation = () => {
    const annotationId = crypto.randomUUID();
    updateSectionAnnotations((annotations) => [
      ...annotations,
      {
        id: annotationId,
        type: "additional",
        visibility_scope: isDeveloper ? "DEV" : "CLIENT",
        is_solved: false,
        text: "",
        author: currentAuthor,
        order: Date.now() + Math.random(),
      },
    ]);

    return { id: annotationId, key: sectionIndex, text: "" };
  };

  const onClickStartEditingComment = (annotation) => {
    setEditingCommentId(annotation.id);
    setDraftText(annotation.text || "");
  }

  const onClickSaveEditingComment = (annotation) => {
    updateAnnotation(annotation.key, annotation.id, { text: draftText });
    setEditingCommentId(null);
    setDraftText("");
  }

  const onClickAddAdditionalComment = () => {
    const newAnnotation = addAdditionalAnnotation();
    if (!newAnnotation) return;
    setEditingCommentId(newAnnotation.id);
    setDraftText(newAnnotation.text || "");
  }

  const onClickCancelEditingComment = () => {
    setEditingCommentId(null);
    setDraftText("");
  }

  const onChangeDraftText = (event) =>
    setDraftText(event.target.value);

  const onClickRemoveComment = (annotation) =>
    removeAnnotation(annotation.key, annotation.id);

  const onChangeResolvedState = (annotation, isResolved) => {
    updateAnnotation(annotation.key, annotation.id, {
      is_solved: isResolved,
    });
  }

  useEffect(() => {
    if (!editingCommentId)
      return;

    const currentComment = sectionAnnotations.find((annotation) => {
      return annotation.id === editingCommentId;
    });

    if (!currentComment) {
      setEditingCommentId(null);
      setDraftText("");
    }
  }, [editingCommentId, sectionAnnotations]);

  return (
    <View
      sectionAnnotations={sectionAnnotations}
      showCommentsPanel={showCommentsPanel}
      editingCommentId={editingCommentId}
      draftText={draftText}
      allowComments={allowComments}
      isDeveloper={isDeveloper}
      showAdditionalCommentButton={showAdditionalCommentButton}
      onClickStartEditingComment={onClickStartEditingComment}
      onClickSaveEditingComment={onClickSaveEditingComment}
      onClickCancelEditingComment={onClickCancelEditingComment}
      onClickAddAdditionalComment={onClickAddAdditionalComment}
      onClickRemoveComment={onClickRemoveComment}
      onChangeDraftText={onChangeDraftText}
      onChangeResolvedState={onChangeResolvedState}
    />
  );

}

export default RevisionAnnotationComments;
